import isDefined from 'isDefined';
import isCustomRangeInput from 'isCustomRangeInput';
import mapGraphqlDayNumberToString from 'mapGraphqlDayNumberToString';
import formatDateLabel from 'components/V5Gadget/formatDateLabel';

import getReversedGroupingField from './getReversedGroupingField';
import getOrderedDaysOfWeek from '../getOrderedDaysOfWeek';
import { getMonthIndex, mapMonthNumberToString } from '../getOrderedMonths';

const buildRowData = ({
  response,
  isTwoDimensional,
  chartDef,
  usedMetrics,
  skip2DRowDataFormatting,
  groupByField,
  skipRaw,
  weekStartsOn,
  formatMetric,
  reverseAxes = false,
  totalsResponse,
}: {
  response: { [key: string]: number | string }[];
  isTwoDimensional: boolean;
  chartDef: V5ChartDefinition;
  usedMetrics: Metrics.Metric[];
  skip2DRowDataFormatting?: boolean;
  groupByField: string;
  skipRaw?: boolean;
  weekStartsOn: WeekStartsOn;
  formatMetric: (args: {
    metricId: string;
    value: string | number | null | undefined;
  }) => string;
  reverseAxes?: boolean;
  // Server-side aggregate totals (responses[1]). Used by the reversed-axes
  // Total column so ratio/percentage metrics aggregate correctly instead of
  // being naively summed across the transposed cells.
  totalsResponse?: MetricsResponse;
}) => {
  const orderedResponse = (() => {
    const isDayOfWeek =
      chartDef.dimensionA && chartDef.dimensionA.isGroupByDayOfWeek;
    const isMonth = chartDef.dimensionA && chartDef.dimensionA.isGroupByMonth;

    if (isDayOfWeek) {
      const orderedDays = getOrderedDaysOfWeek(weekStartsOn);
      return orderedDays
        .map((day) => {
          return response.find(
            (r) =>
              mapGraphqlDayNumberToString(r['dayOfWeek'].toString()) === day,
          );
        })
        .filter(isDefined);
    }

    if (isMonth) {
      return [...response].sort((a, b) => {
        const monthA = getMonthIndex(String(a['month']));
        const monthB = getMonthIndex(String(b['month']));
        if (monthA !== -1 && monthB !== -1) {
          return monthA - monthB;
        }
        return Number(a['month']) - Number(b['month']);
      });
    }

    return response;
  })();

  // Handle reversed axes - transpose the data structure
  if (reverseAxes) {
    if (isTwoDimensional) {
      // 2D reversed: each dimensionB value becomes a row
      const metricId = chartDef.series[0].metricId;
      const metric = usedMetrics.find((m) => m.id === metricId);
      if (!metric) {
        throw new Error(`Metric ${metricId} not found`);
      }

      // Get dimension B field (the reversed grouping column)
      const dimensionBField = getReversedGroupingField({
        chartDef,
        isTwoDimensional,
      });

      // Collect all unique dimensionB values (these will become rows)
      const dimensionBValues: string[] = [];
      const dataMap = new Map<
        string,
        Map<string, number | string | undefined>
      >();

      orderedResponse.forEach((r) => {
        const dimensionAValue = r[groupByField] as string;

        if (r.values) {
          Object.values(r.values).forEach((v: any) => {
            let dimensionBValue: string;

            if (chartDef.dimensionB?.fieldType === 'date') {
              dimensionBValue = v.date;
            } else if (chartDef.dimensionB?.isGroupByDayOfWeek) {
              dimensionBValue = mapGraphqlDayNumberToString(
                v['dayOfWeek'].toString(),
              );
            } else if (chartDef.dimensionB?.isGroupByMonth) {
              dimensionBValue = mapMonthNumberToString(v['month'].toString());
            } else if (chartDef.dimensionB?.rangeInput && v.key) {
              dimensionBValue = v.key.toString();
            } else {
              dimensionBValue = v[dimensionBField] as string;
            }

            if (!dimensionBValues.includes(dimensionBValue)) {
              dimensionBValues.push(dimensionBValue);
            }

            if (!dataMap.has(dimensionBValue)) {
              dataMap.set(dimensionBValue, new Map());
            }

            dataMap.get(dimensionBValue)?.set(dimensionAValue, v[metricId]);
          });
        }
      });

      // Sort dimensionBValues chronologically for dayOfWeek/month
      if (chartDef.dimensionB?.isGroupByDayOfWeek) {
        const orderedDays = getOrderedDaysOfWeek(weekStartsOn);
        dimensionBValues.sort((a, b) => {
          const indexA = orderedDays.indexOf(a);
          const indexB = orderedDays.indexOf(b);
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          return 0;
        });
      }

      if (chartDef.dimensionB?.isGroupByMonth) {
        dimensionBValues.sort((a, b) => {
          const indexA = getMonthIndex(a);
          const indexB = getMonthIndex(b);
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          return 0;
        });
      }

      // Per-dimensionB aggregate total, keyed by the same dimensionB value the
      // rows use. Sourced from the server-side totals query (responses[1]) so
      // ratio/percentage metrics aggregate correctly instead of being summed.
      const totalByDimensionBValue = new Map<string, number | undefined>();
      (totalsResponse || []).forEach((entry) => {
        let key: string;
        if (chartDef.dimensionB?.fieldType === 'date') {
          key = entry.date as string;
        } else if (chartDef.dimensionB?.isGroupByDayOfWeek) {
          key = mapGraphqlDayNumberToString(entry['dayOfWeek'].toString());
        } else if (chartDef.dimensionB?.isGroupByMonth) {
          key = mapMonthNumberToString(entry['month'].toString());
        } else if (chartDef.dimensionB?.rangeInput && entry.key !== undefined) {
          key = entry.key.toString();
        } else {
          key = entry[dimensionBField] as string;
        }
        const value = entry[metricId];
        totalByDimensionBValue.set(
          key,
          typeof value === 'number' ? value : undefined,
        );
      });

      // Build rows: each dimensionB value is a row
      const result = dimensionBValues.map((dimensionBValue) => {
        const row: { [key: string]: string | number | undefined } = {};
        row[dimensionBField] = dimensionBValue;

        const values = dataMap.get(dimensionBValue);

        values?.forEach((value, dimensionAValue) => {
          if (!skipRaw) {
            row[`${dimensionAValue}-raw`] = value;
          }
          if (skip2DRowDataFormatting) {
            row[dimensionAValue] = value;
          } else {
            row[dimensionAValue] = formatMetric({
              value,
              metricId,
            });
          }
        });

        // Add total column from the server aggregate (not a client-side sum)
        const totalRaw = totalByDimensionBValue.get(dimensionBValue);
        if (!skipRaw) {
          row['total-raw'] = totalRaw;
        }
        row['total'] =
          totalRaw === undefined
            ? ''
            : formatMetric({
                value: totalRaw,
                metricId,
              });

        return row;
      });

      return result;
    } else {
      // 1D reversed: each metric becomes a row
      // groupByField parameter already contains the dimensionA field

      // Single-row server aggregate keyed by metric id (responses[1]).
      const totalsRow = (totalsResponse || [])[0];

      const result = chartDef.series.map((s) => {
        const metric = usedMetrics.find((m) => m.id === s.metricId);
        if (!metric) {
          throw new Error(`Metric ${s.metricId} not found`);
        }

        const row: { [key: string]: string | number | undefined } = {};
        row['metric'] = s.displayName ? s.displayName : metric.name;

        orderedResponse.forEach((r) => {
          const dimensionAValue = r[groupByField] as string;
          const metricValue = r[metric.id];

          if (!skipRaw) {
            row[`${dimensionAValue}-raw`] = metricValue;
          }
          row[dimensionAValue] = formatMetric({
            value: metricValue,
            metricId: metric.id,
          });
        });

        // Add total column from the server aggregate (not a client-side sum).
        // Metrics with "Show Total" disabled are absent from the totals query,
        // so their total is blanked (matching the normal-axes behaviour).
        const totalRaw =
          s.isTotalEnabled === false ? undefined : totalsRow?.[metric.id];
        const totalIsNumber = typeof totalRaw === 'number';
        if (!skipRaw) {
          row['total-raw'] = totalIsNumber ? totalRaw : undefined;
        }
        row['total'] = totalIsNumber
          ? formatMetric({
              value: totalRaw,
              metricId: metric.id,
            })
          : '';

        return row;
      });

      return result;
    }
  }

  // Original logic (unchanged)
  return Object.values(orderedResponse).map((r) => {
    if (isTwoDimensional) {
      const metricId = chartDef.series[0].metricId;
      const metric = usedMetrics.find((m) => m.id === metricId);
      if (!metric) {
        throw new Error(`Metric ${metricId} not found`);
      }

      const formatted = {} as { [key: string]: string | number | undefined };
      formatted[groupByField] = r[groupByField];
      if (chartDef.secondaryDimensionA) {
        const secondaryField = chartDef.secondaryDimensionA.field;
        formatted[secondaryField] = r[secondaryField];
      }
      Object.values(r.values).forEach((v) => {
        if (chartDef.dimensionB && chartDef.dimensionB.rangeInput && v.key) {
          if (!skipRaw) {
            formatted[`${v.key.toString()}-raw`] = v[metricId];
          }
          if (skip2DRowDataFormatting) {
            formatted[v.key.toString()] = v[metricId] as string;
          } else {
            formatted[v.key.toString()] = formatMetric({
              value: v[metricId],
              metricId,
            });
          }
        }

        if (chartDef.dimensionB && chartDef.dimensionB.fieldType === 'date') {
          if (!skipRaw) {
            formatted[`${v.date}-raw`] = v[metricId];
          }
          if (skip2DRowDataFormatting) {
            formatted[v.date] = v[metricId] as string;
          } else {
            formatted[v.date] = formatMetric({
              value: v[metricId],
              metricId,
            });
          }
        }

        if (chartDef.dimensionB && chartDef.dimensionB.isGroupByDayOfWeek) {
          const dayOfWeek = mapGraphqlDayNumberToString(
            v['dayOfWeek'].toString(),
          );
          if (!skipRaw) {
            formatted[`${dayOfWeek}-raw`] = v[metricId];
          }
          if (skip2DRowDataFormatting) {
            formatted[dayOfWeek] = v[metricId] as string;
          } else {
            formatted[dayOfWeek] = formatMetric({
              value: v[metricId],
              metricId,
            });
          }
        }

        if (chartDef.dimensionB && chartDef.dimensionB.isGroupByMonth) {
          const month = mapMonthNumberToString(v['month'].toString());
          if (!skipRaw) {
            formatted[`${month}-raw`] = v[metricId];
          }
          if (skip2DRowDataFormatting) {
            formatted[month] = v[metricId] as string;
          } else {
            formatted[month] = formatMetric({
              value: v[metricId],
              metricId,
            });
          }
        }

        // Categorical second dimension (terms x terms via subGroupBy). The
        // inner cell is keyed by dimensionB.field; blanks bucket as "(Blank)".
        if (
          chartDef.dimensionB &&
          !chartDef.dimensionB.rangeInput &&
          chartDef.dimensionB.fieldType !== 'date' &&
          !chartDef.dimensionB.isGroupByDayOfWeek &&
          !chartDef.dimensionB.isGroupByMonth
        ) {
          const subField = chartDef.dimensionB.field;
          const rawKey = v[subField];
          const key =
            rawKey === undefined || rawKey === null || rawKey === ''
              ? '(Blank)'
              : rawKey.toString();
          if (!skipRaw) {
            formatted[`${key}-raw`] = v[metricId];
          }
          if (skip2DRowDataFormatting) {
            formatted[key] = v[metricId] as string;
          } else {
            formatted[key] = formatMetric({
              value: v[metricId],
              metricId,
            });
          }
        }

        if (chartDef.dimensionA && chartDef.dimensionA.rangeInput) {
          if (isCustomRangeInput(chartDef.dimensionA.rangeInput)) {
            formatted[groupByField] = r['key'];
          }
        }
      });
      return formatted;
    } else {
      const formatted = { ...r };
      Object.entries(r).forEach(([key, value]) => {
        const metric = usedMetrics.find((m) => m.id === key);
        if (metric) {
          if (!skipRaw) {
            formatted[`${key}-raw`] = value;
          }

          formatted[key] = formatMetric({
            value,
            metricId: metric.id,
          });
        } else {
          if (key === 'dayOfWeek' && !!chartDef.groupByDayOfWeek) {
            formatted[key] = mapGraphqlDayNumberToString(value.toString());
          } else if (key === 'month' && !!chartDef.groupByMonth) {
            formatted[key] = mapMonthNumberToString(value.toString());
          } else if (key === 'date' && groupByField === 'day') {
            formatted['day'] = value;
          } else if (
            key === 'date' &&
            groupByField === 'date' &&
            chartDef.trendByCalendarInterval
          ) {
            formatted['date'] = formatDateLabel(
              value,
              chartDef.trendByCalendarInterval,
              false,
              undefined,
              chartDef.gadgetType === 'matrix',
              false,
              chartDef.gadgetType === 'matrix',
            );
            formatted['raw-date'] = value;
          } else if (
            chartDef.dimensionA &&
            chartDef.dimensionA.rangeInput &&
            key === 'key'
          ) {
            const rangeInput = chartDef.dimensionA.rangeInput;
            if (isCustomRangeInput(rangeInput)) {
              formatted[chartDef.dimensionA.field] = value;
            } else {
              const { fixed } = rangeInput;
              if (fixed !== 1) {
                formatted[chartDef.dimensionA.field] = `${value} - ${
                  Number(value) + fixed
                }`;
              } else {
                formatted[chartDef.dimensionA.field] = `${value}`;
              }
            }
          } else {
            if (!skipRaw) {
              formatted[`${key}-raw`] = value;
            }
            formatted[key] = value;
          }
        }
      });

      return formatted;
    }
  });
};

export default buildRowData;
